import { lazy, Suspense } from 'react';
import { createBrowserRouter, redirect, Outlet } from 'react-router-dom';
import { MainLayout } from '@/widgets/layout';
import { authService } from '@/features/auth/model/auth-service';
import { ROUTER_PATHS } from './routes';

// Lazy load pages for code splitting
const HomePage = lazy(() => import('@/pages/home-page').then((m) => ({ default: m.HomePage })));
const LoginPage = lazy(() => import('@/pages/login-page').then((m) => ({ default: m.LoginPage })));
const PoDetailPage = lazy(() => import('@/pages/po-detail-page').then((m) => ({ default: m.PoDetailPage })));

const PoLineDetailPage = lazy(() =>
	import('@/pages/po-line-detail-page').then((m) => ({ default: m.PoLineDetailPage }))
);
const PoListPage = lazy(() => import('@/pages/po-list-page').then((m) => ({ default: m.PoListPage })));
const ItpPage = lazy(() => import('@/pages/itp-page').then((m) => ({ default: m.ItpPage })));

/**
 * Protected route loader - redirects to /login if not authenticated
 */
async function protectedLoader() {
	const isAuthenticated = await authService.isAuthenticated();

	if (!isAuthenticated) {
		return redirect(ROUTER_PATHS.login.getLink());
	}
	return null;
}

/**
 * Login page loader - redirects to / if already authenticated
 */
async function loginPageLoader() {
	const isAuthenticated = await authService.isAuthenticated();

	if (isAuthenticated) {
		return redirect(ROUTER_PATHS.root.getLink());
	}
	return null;
}

/**
 * Create router - must be called AFTER auth is initialized
 */
export function createRouter() {
	return createBrowserRouter([
		{
			path: '/',
			element: <Outlet />,
			children: [
				// Protected routes (with MainLayout)
				{
					loader: protectedLoader,
					element: <MainLayout />,
					children: [
						{
							index: true,
							element: (
								<Suspense fallback={null}>
									<HomePage />
								</Suspense>
							),
						},
						{
							path: ROUTER_PATHS.poDetail.path,
							handle: {
								crumb: (params: Record<string, string | undefined>) => [
									{ label: 'Purchase Orders', path: ROUTER_PATHS.purchaseOrders.getLink() },
									{ label: params.poLabel ?? params.poId!, path: ROUTER_PATHS.poDetail.getLink(params.poId!) },
								],
							},
							element: (
								<Suspense fallback={null}>
									<PoDetailPage />
								</Suspense>
							),
						},
						{
							path: ROUTER_PATHS.poLineDetail.path,
							handle: {
								crumb: (params: Record<string, string | undefined>) => [
									{ label: 'Purchase Orders', path: ROUTER_PATHS.purchaseOrders.getLink() },
									{ label: params.poLabel ?? params.poId!, path: ROUTER_PATHS.poDetail.getLink(params.poId!) },
									{
										label: params.lineLabel ?? params.lineId!,
										path: ROUTER_PATHS.poLineDetail.getLink(params.poId!, params.lineId!),
									},
								],
							},
							element: (
								<Suspense fallback={null}>
									<PoLineDetailPage />
								</Suspense>
							),
						},
						{
							path: ROUTER_PATHS.itpDetail.path,
							handle: {
								crumb: (params: Record<string, string | undefined>) => [
									{ label: 'Purchase Orders', path: ROUTER_PATHS.purchaseOrders.getLink() },
									{ label: params.poLabel ?? params.poId!, path: ROUTER_PATHS.poDetail.getLink(params.poId!) },
									{ label: 'ITP' },
								],
							},
							element: (
								<Suspense fallback={null}>
									<ItpPage />
								</Suspense>
							),
						},
						{
							path: ROUTER_PATHS.purchaseOrders.path,
							handle: {
								crumb: () => [{ label: 'Purchase Orders', path: ROUTER_PATHS.purchaseOrders.getLink() }],
							},
							element: (
								<Suspense fallback={null}>
									<PoListPage />
								</Suspense>
							),
						},
					],
				},
				// Public routes
				{
					path: ROUTER_PATHS.login.path,
					loader: loginPageLoader,
					element: (
						<Suspense fallback={null}>
							<LoginPage />
						</Suspense>
					),
				},
			],
		},
		// Catch-all: redirect unknown paths to root
		{
			path: '*',
			loader: () => redirect(ROUTER_PATHS.root.getLink()),
		},
	]);
}
